import { useEffect, useState, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Kanban, House, Buildings, Bell, ListChecks, ClipboardText, ArchiveTrayIcon, Vault } from '@phosphor-icons/react';
import { Newspaper } from '@phosphor-icons/react';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canManagePlanner } from '@/auth/AuthContext';
import { plannerApi } from './plannerApi';
import { groupDepartments } from './plannerConstants';
import { sidebarItemClass as linkClass, sidebarPillClass as pillClass } from '@/components/enterprise/ModuleSidebar';

export default function PlannerLayout() {
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentDeptId = (location.pathname.match(/^\/planner\/dept\/([^/]+)/) || [])[1] || '';
  const [departments, setDepartments] = useState([]);
  const [alerts, setAlerts] = useState({ total: 0, my_open_count: 0, my_blocked_count: 0 });

  const load = useCallback(() => {
    plannerApi.departments().then(setDepartments).catch(() => {});
    plannerApi.alerts().then(setAlerts).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    /* U6 — ページ自体のスクロールだけを使う。入れ子のスクロール領域が
       #root を画面より高くして、二重のスクロールバーを出していた。 */
    <div className="flex flex-col md:flex-row gap-5 md:gap-6" data-testid="planner-layout">
      <aside className="md:w-64 shrink-0">
        {/* ── Mobile: compact pill nav + department picker ── */}
        <div className="md:hidden space-y-2" data-testid="planner-mobile-nav">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
            <NavLink to="/planner" end className={pillClass} data-testid="planner-nav-home-m">
              <House size={15} weight="duotone" /> {t('planner.nav.home')}
            </NavLink>
            <NavLink to="/planner/my-tasks" className={pillClass} data-testid="planner-nav-mytasks-m">
              <ListChecks size={15} weight="duotone" /> {t('planner.nav.myTasks')}
            </NavLink>
            <NavLink to="/planner/my-projects" className={pillClass} data-testid="planner-nav-myprojects-m">
              <ClipboardText size={15} weight="duotone" /> {t('planner.nav.myProjects')}
            </NavLink>
            <NavLink to="/planner/digest" className={pillClass} data-testid="planner-nav-digest-m">
              <Newspaper size={15} weight="duotone" /> {t('planner.nav.digest')}
            </NavLink>
            <NavLink to="/planner/old-projects" className={pillClass} data-testid="planner-nav-oldproj-m">
              <Vault size={15} weight="duotone" /> {t('planner.oldproj.title')}
            </NavLink>
            {canManagePlanner(user) && (
              <NavLink to="/planner/archive-review" className={pillClass} data-testid="planner-nav-archive-m">
                <ArchiveTrayIcon size={15} weight="duotone" /> {t('planner.archive.reviewTitle')}
              </NavLink>
            )}
          </div>
          <select
            value={currentDeptId}
            onChange={(e) => e.target.value && navigate(`/planner/dept/${e.target.value}`)}
            data-testid="planner-dept-select-m"
            className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2.5 text-sm text-zinc-700 outline-none focus:border-teal-600"
          >
            <option value="">{`${t('planner.nav.departments')}を選択…`}</option>
            {groupDepartments(departments).map((g) => (
              <optgroup key={g.key} label={g.label}>
                {g.items.map((d) => (
                  <option key={d.id} value={d.id}>{`${d.name}${d.active_count > 0 ? `（${d.active_count}）` : ''}`}</option>
                ))}
              </optgroup>
            ))}
          </select>
          {(alerts.my_open_count || 0) > 0 && (
            <button onClick={() => navigate('/planner/my-tasks')} data-testid="planner-alert-pill-m"
              className="w-full flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 active:bg-rose-100 transition-colors">
              <Bell size={16} weight="fill" /> {t('planner.alerts.pill', { n: alerts.my_open_count })}
              {(alerts.my_blocked_count || 0) > 0 && (
                <span className="ml-auto text-[11px] font-semibold" data-testid="planner-alert-pill-blocked-m">
                  {t('planner.alerts.blockedNote', { n: alerts.my_blocked_count })}
                </span>
              )}
            </button>
          )}
        </div>

        {/* ── Desktop sidebar ── */}
        <div className="hidden md:block md:sticky md:top-24 md:max-h-[calc(100dvh-7.5rem)] md:overflow-y-auto space-y-4 md:pr-1">
          <div className="px-3">
            <div className="flex items-center gap-2 text-zinc-900">
              <Kanban size={18} weight="duotone" className="text-teal-700" />
              <h2 className="text-base font-semibold tracking-tight">{t('app.planner')}</h2>
            </div>
          </div>

          <nav className="space-y-0.5">
            <NavLink to="/planner" end className={linkClass} data-testid="planner-nav-home">
              <House size={18} weight="duotone" className="shrink-0" /> {t('planner.nav.home')}
            </NavLink>
            <NavLink to="/planner/my-tasks" className={linkClass} data-testid="planner-nav-mytasks">
              <ListChecks size={18} weight="duotone" className="shrink-0" /> {t('planner.nav.myTasks')}
            </NavLink>
            <NavLink to="/planner/my-projects" className={linkClass} data-testid="planner-nav-myprojects">
              <ClipboardText size={18} weight="duotone" className="shrink-0" /> {t('planner.nav.myProjects')}
            </NavLink>
            <NavLink to="/planner/digest" className={linkClass} data-testid="planner-nav-digest">
              <Newspaper size={18} weight="duotone" className="shrink-0" /> {t('planner.nav.digest')}
            </NavLink>
            <NavLink to="/planner/old-projects" className={linkClass} data-testid="planner-nav-oldproj">
              <Vault size={18} weight="duotone" className="shrink-0" /> {t('planner.oldproj.title')}
            </NavLink>
            {canManagePlanner(user) && (
              <NavLink to="/planner/archive-review" className={linkClass} data-testid="planner-nav-archive">
                <ArchiveTrayIcon size={18} weight="duotone" className="shrink-0" /> {t('planner.archive.reviewTitle')}
              </NavLink>
            )}
            <div className="px-3 pt-3 pb-1 text-[11px] font-semibold text-zinc-400">{t('planner.nav.departments')}</div>
            {groupDepartments(departments).map((g) => (
              <div key={g.key} className="pt-1">
                <div className="px-3 py-1 text-[11px] font-semibold text-zinc-400">{g.label}</div>
                {g.items.map((d) => (
                  <NavLink key={d.id} to={`/planner/dept/${d.id}`} className={linkClass} title={d.name}
                    aria-label={d.name} data-testid={`planner-nav-dept-${d.id}`}>
                    <Buildings size={17} weight="duotone" className="shrink-0 text-zinc-400" />
                    <span className="truncate flex-1">{d.name}</span>
                    {d.active_count > 0 && <span className="text-[10px] tabular-nums text-zinc-400">{d.active_count}</span>}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          {(alerts.my_open_count || 0) > 0 && (
            <button onClick={() => navigate('/planner/my-tasks')} data-testid="planner-alert-pill"
              className="w-full flex flex-col items-start gap-0.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 hover:bg-rose-100 transition-colors">
              <span className="flex items-center gap-2">
                <Bell size={16} weight="fill" /> {t('planner.alerts.pill', { n: alerts.my_open_count })}
              </span>
              {(alerts.my_blocked_count || 0) > 0 && (
                <span className="text-[11px] font-semibold pl-6" data-testid="planner-alert-pill-blocked">
                  {t('planner.alerts.blockedNote', { n: alerts.my_blocked_count })}
                </span>
              )}
            </button>
          )}
        </div>
      </aside>
      <div className="flex-1 min-w-0 pb-24" data-testid="planner-main-scroll">
        <Outlet context={{ reloadSidebar: load }} />
      </div>
    </div>
  );
}
